import { Banner, Box, FlexBox, Text, Tooltip } from '@dixa/lib-shared-habla';
import type { WidgetSettingsItem } from 'lib-integrations-partner-widgets';

import styles from './integrationCardSummary.module.scss';

type IntegrationsCardSummaryProps = {
  /**
   * Total widgets eligible to render (post feature-flag / enabled /
   * per-user-hidden).
   */
  totalCount: number;
  /**
   * Number of widgets currently visible _because their non-empty render
   * conditions matched_. Excludes widgets with no conditions at all.
   */
  matchedCount: number;
  /** Eligible widgets that are filtered out by their render conditions. */
  hiddenWidgets: readonly WidgetSettingsItem[];
  /** Whether the agent has clicked the "Show" link to reveal the hidden ones. */
  isForceShown: boolean;
  /** Toggles the force-show state. */
  onToggleForceShow: () => void;
};

/**
 * Compact header for the native-integration section showing how many widgets
 * are visible vs hidden by render conditions. Hidden ones can be temporarily
 * revealed via the inline "(show)" link; the state is component-local and not
 * persisted, so a sidebar remount reverts to the default (hidden again).
 */
export const IntegrationsCardSummary = ({
  totalCount,
  matchedCount,
  hiddenWidgets,
  isForceShown,
  onToggleForceShow,
}: IntegrationsCardSummaryProps): JSX.Element | null => {
  const hiddenCount = hiddenWidgets.length;
  const visibleCount = isForceShown ? totalCount : totalCount - hiddenCount;

  // Hide the summary entirely when there are no conditional widgets — i.e.
  // every integration is shown unconditionally. The "X of Y shown" line on
  // its own carries no useful information in that case.
  if (totalCount === 0 || (matchedCount === 0 && hiddenCount === 0)) {
    return null;
  }

  return (
    <Box padding="200 300">
      <Tooltip content="Conditional render summary">
        <Banner
          variant="info"
          icon="filter"
          description={
            <FlexBox gap="100" flexDirection="column">
              <Text.Paragraph variant="4" subdued>
                {visibleCount} of {totalCount} integrations shown
              </Text.Paragraph>

              {matchedCount > 0 && (
                <Text.Paragraph variant="4" subdued>
                  {matchedCount} matched the conditions
                </Text.Paragraph>
              )}

              {hiddenCount > 0 && (
                <Text.Paragraph variant="4" subdued>
                  {hiddenCount} hidden{' '}
                  <Tooltip
                    content={
                      <Text.Paragraph variant="4" color="neutral.000">
                        {hiddenWidgets.map((widget) => widget.name).join(', ')}
                      </Text.Paragraph>
                    }
                  >
                    <span
                      className={styles.toggleLink}
                      onClick={onToggleForceShow}
                    >
                      ({isForceShown ? 'hide' : 'show'})
                    </span>
                  </Tooltip>
                </Text.Paragraph>
              )}
            </FlexBox>
          }
        />
      </Tooltip>
    </Box>
  );
};
